<template>
  <div class="time-modifications">
    <SettingsHeader :title="$t('settings.timeModifications.heading')" />
    <AntForm
      :form="form"
      @submit.prevent="handleSubmit"
      class="time-modifications__content"
    >
      <span class="time-modifications__instruction">
        {{ $t('settings.timeModifications.instruction') }}
      </span>
      <div class="time-modifications__select-wrapper">
        <Select
          name="settingPath"
          :label="$t('settings.timeModifications.title')"
          :placeholder="$t('settings.timeModifications.fieldPlaceholders.settingPath')"
          @change="handleSettingPathChange"
        >
          <AntOption :value="'area'">
            {{ $t('settings.timeModifications.options.area') }}
          </AntOption>
          <AntOption
            v-for="(value, key) in settings.spec"
            :key="key"
            :value="`spec.${key}`"
          >
            {{ $t(`settings.timeModifications.options.spec.${key}`) }}
          </AntOption>
          <AntOption
            v-for="(value, key) in settings.general"
            :key="key"
            :value="`general.${key}`"
          >
            {{ $t(`settings.timeModifications.options.general.${key}`) }}
          </AntOption>
          <AntOption
            v-for="(value, key) in settings.multipliers"
            :key="key"
            :value="`multipliers.${key}`"
          >
            {{ $t(`settings.timeModifications.options.multipliers.${key}`) }}
          </AntOption>
        </Select>
        <div v-if="settingToEdit.selected" class="time-modifications__modifications">
          <div class="time-modifications__modifications-list">
            <span class="time-modifications__instruction">
              {{
                $t(`settings.timeModifications.${settingToEdit.multipleValues
                  ? 'modInstructionTime'
                  : 'modInstructionGeneric' }`)
              }}
            </span>
            <template v-if="settingToEdit.multipleValues">
              <Input
                v-for="({ label, value }, index) in settingToEdit.value"
                :key="`${settingToEdit.key}.${label}`"
                :name="`${settingToEdit.key}[${index}].${label}`"
                :label="label"
                :initialValue="value"
                :preserve="false"
                inputType="number"
                class="time-modifications__modification time-modifications__modification--multiple"
              />
            </template>
            <Input
              v-else
              :name="settingToEdit.key"
              :initialValue="settingToEdit.value"
              :preserve="false"
              :precision="settingToEdit.group === 'multipliers' ? 2 : undefined"
              inputType="number"
              class="time-modifications__modification time-modifications__modification--single"
            />
          </div>
          <div class="time-modifications__buttons-wrapper">
            <!-- @todo uncomment once edit mode is implemented -->
            <!-- <Button class="time-modifications__cancel">
              {{ $t('settings.timeModifications.buttons.cancel') }}
            </Button> -->
            <Button type="submit" :loading="loading">
              {{ $t('settings.timeModifications.buttons.save') }}
            </Button>
          </div>
        </div>
      </div>
    </AntForm>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import {
  Select as AntSelect,
  Form as AntForm,
} from 'ant-design-vue';

import SettingsHeader from './SettingsHeader';
import { defaultSettings } from '@/common/config';
import { Input, Button, Select } from '@/components/common';
import { getProp, orderByKeys } from '@/common/helpers';

const { mapState, mapActions } = createNamespacedHelpers('estimate');

export default {
  name: 'TimeModifications',
  components: {
    Input,
    Button,
    Select,
    AntForm,
    AntOption: AntSelect.Option,
    SettingsHeader,
  },
  data() {
    return {
      settingToEdit: {
        selected: false,
        key: null,
        value: null,
        group: null,
        multipleValues: false,
      },
      loading: false,
      form: this.$form.createForm(this, {}),
    };
  },
  computed: {
    ...mapState({
      settings: (state) => {
        const { settings: { data } } = state;
        const {
          multipliers,
          spec,
          revised_hourly_rate,
          area,
          ...rest
        } = data;
        // We ignore revised_hourly_rate as we dont want user to change that
        return {
          multipliers: orderByKeys(multipliers, Object.keys(defaultSettings.multipliers)),
          spec: orderByKeys(spec, Object.keys(defaultSettings.spec)),
          area,
          general: rest,
        };
      },
    }),
  },
  methods: {
    ...mapActions(['updateSettings']),
    formatOptions(options) {
      return options.map((option) => {
        const [label, value] = Object.entries(option)[0];
        return { label, value };
      });
    },
    handleSettingPathChange(settingPath) {
      const value = getProp(this.settings, settingPath, null);
      const [group, key = 'value'] = settingPath.split('.');
      this.settingToEdit = {
        selected: true,
        multipleValues: Array.isArray(value),
        key,
        group,
        value,
      };
    },
    async handleSubmit() {
      try {
        this.loading = true;

        const formValues = this.form.getFieldsValue();
        const value = this.settingToEdit.multipleValues
          ? this.formatOptions(formValues[this.settingToEdit.key])
          : formValues[this.settingToEdit.key];
        const path = formValues.settingPath.replace('general.', '');

        await this.updateSettings({ path, value });
        this.$notifications.success(this.$t('settings.timeModifications.successMsg'));
      } catch (error) {
        this.$notifications.error(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};

</script>

<style lang="scss">
  .time-modifications {
    width: 100%;

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      width: 100%;

      @include md-up {
        margin: 0 auto;
        padding: 60px 0;
        width: 500px;
      }
    }

    &__instruction {
      color: $secondary;
      display: block;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: .19px;
      margin-bottom: 40px;
    }

    &__select-wrapper {
      width: 100%;
    }

    &__modifications {
      border-radius: 12px;
      border: solid 1px $grayLightest;
      overflow: hidden;
      width: 100%;
    }

    &__modifications-list {
      padding: 32px 20px 8px;
      text-align: center;
    }

    &__modification {
      margin: 0 auto 12px;
      max-width: 330px;

      @include md-up {
        max-width: none;
      }

      .ant-form-item-children {
        align-items: center;
        display: flex;
        justify-content: space-between;

        label {
          line-height: normal;
          margin-right: 12px;
          text-align: left;
        }
      }

      .ant-input-number {
        flex: 0 0 100px;
        margin: 0;
      }

      &--single {
        .ant-form-item-children {
          justify-content: center;
        }
      }
    }

    &__buttons-wrapper {
      @include center-content;
      background-color: $stepperBackground;
      height: 100px;

      .ant-btn {
        width: 100px;
      }
    }

    &__cancel {
      background-color: $white;
      border: 1px solid $grayLight;
      color: $primary;
      margin-right: 24px;

      &:hover {
        background-color: darken($white, 3%);
        transition: all .6s;
      }
    }

    .ant-form-item,
    .ant-select {
      width: 100%;
    }
  }
</style>
